import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, Code, AlertCircle, CheckCircle } from 'lucide-react';
import { apiService } from '../../services/apiService';

const AdminTracking = () => {
  const [trackingCodes, setTrackingCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingCode, setEditingCode] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    location: 'head',
    active: true
  });

  // Predefined tracking code templates
  const codeTemplates = {
    'google-analytics': {
      name: 'Google Analytics 4',
      code: `<!-- Google tag (gtag.js) -->
<script async src="https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'GA_MEASUREMENT_ID');
</script>`,
      location: 'head'
    },
    'yandex-metrika': {
      name: 'Yandex Metrika',
      code: `<!-- Yandex.Metrika counter -->
<script type="text/javascript">
   (function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
   m[i].l=1*new Date();k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})
   (window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");

   ym(YANDEX_ID, "init", {
        clickmap:true,
        trackLinks:true,
        accurateTrackBounce:true
   });
</script>`,
      location: 'head'
    },
    'facebook-pixel': {
      name: 'Facebook Pixel',
      code: `<!-- Facebook Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', 'YOUR_PIXEL_ID');
fbq('track', 'PageView');
</script>`,
      location: 'head'
    }
  };

  useEffect(() => {
    loadTrackingCodes();
  }, []);

  const loadTrackingCodes = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTrackingCodes();
      setTrackingCodes(response || []);
    } catch (error) {
      setError('Ошибка загрузки кодов трекинга');
      console.error('Error loading tracking codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCode) {
        await apiService.updateTrackingCode(editingCode.id, formData);
        setSuccess('Код трекинга обновлен');
      } else {
        await apiService.createTrackingCode(formData);
        setSuccess('Код трекинга создан');
      }
      
      resetForm();
      setIsModalOpen(false);
      loadTrackingCodes();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Ошибка сохранения кода трекинга');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleEdit = (code) => {
    setEditingCode(code);
    setFormData({
      name: code.name,
      code: code.code,
      location: code.location,
      active: code.active
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (codeId) => {
    if (window.confirm('Вы уверены, что хотите удалить этот код трекинга?')) {
      try {
        await apiService.deleteTrackingCode(codeId);
        setSuccess('Код трекинга удален');
        loadTrackingCodes();
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        setError('Ошибка удаления кода трекинга');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const toggleActive = async (code) => {
    try {
      await apiService.updateTrackingCode(code.id, { active: !code.active });
      setSuccess(code.active ? 'Код деактивирован' : 'Код активирован');
      loadTrackingCodes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Ошибка изменения статуса');
      setTimeout(() => setError(''), 3000);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      location: 'head',
      active: true
    });
    setEditingCode(null);
  };

  const loadTemplate = (templateKey) => {
    const template = codeTemplates[templateKey];
    setFormData({
      name: template.name,
      code: template.code,
      location: template.location,
      active: true
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Коды трекинга</h2>
          <p className="text-gray-400 mt-1">
            Управление счетчиками посетителей и аналитикой сайта
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Добавить код</span>
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-900 border border-green-700 text-green-100 px-4 py-3 rounded-lg flex items-center space-x-2">
          <CheckCircle className="w-5 h-5" />
          <span>{success}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-900 border border-red-700 text-red-100 px-4 py-3 rounded-lg flex items-center space-x-2">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Tracking Codes Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Название
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Расположение
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Статус
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {trackingCodes.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-400">
                    <Code className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    Коды трекинга не найдены
                  </td>
                </tr>
              ) : (
                trackingCodes.map((code) => (
                  <tr key={code.id} className="hover:bg-gray-750">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-white">{code.name}</div>
                      <div className="text-sm text-gray-400">
                        {new Date(code.created_at).toLocaleDateString('ru-RU')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-700 text-gray-300">
                        {code.location === 'head' ? '<head>' : 
                         code.location === 'body_start' ? '<body> start' : '<body> end'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleActive(code)}
                        className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full transition-colors ${
                          code.active 
                            ? 'bg-green-900 text-green-100 hover:bg-green-800' 
                            : 'bg-red-900 text-red-100 hover:bg-red-800'
                        }`}
                      >
                        {code.active ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                        {code.active ? 'Активен' : 'Неактивен'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleEdit(code)}
                        className="text-indigo-400 hover:text-indigo-300 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(code.id)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for Add/Edit Tracking Code */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-medium text-white mb-4">
                {editingCode ? 'Редактировать код трекинга' : 'Добавить код трекинга'}
              </h3>

              {/* Templates */}
              {!editingCode && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Быстрые шаблоны
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {Object.entries(codeTemplates).map(([key, template]) => (
                      <button
                        key={key}
                        onClick={() => loadTemplate(key)}
                        className="p-2 text-left text-sm bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                      >
                        {template.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Название *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                    placeholder="Google Analytics, Яндекс.Метрика и т.д."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Расположение кода
                  </label>
                  <select
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="head">&lt;head&gt; секция (рекомендуется)</option>
                    <option value="body_start">Начало &lt;body&gt;</option>
                    <option value="body_end">Конец &lt;body&gt;</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Код трекинга *
                  </label>
                  <textarea
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                    rows={12}
                    required
                    placeholder="Вставьте здесь код трекинга/счетчика..."
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Скопируйте и вставьте код полностью, включая теги &lt;script&gt;
                  </p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData({...formData, active: e.target.checked})}
                    className="h-4 w-4 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500 focus:ring-2"
                  />
                  <label htmlFor="active" className="ml-2 text-sm text-gray-300">
                    Активировать код сразу после сохранения
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                  >
                    {editingCode ? 'Обновить' : 'Создать'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTracking;